import React from 'react';
import {t} from '@lingui/core/macro';
import cn from 'classnames';

import Correct from 'shared/ui/icons/General/Correct';
import Wrong from 'shared/ui/icons/General/Wrong';
import ResponseTimeChart from 'shared/ui/ResponseTimeChart/ResponseTimeChart';
import {getFormattedDate} from 'shared/lib/datetime';
import {isTranslate} from 'shared/lib/lang';

import {PALETTE} from 'shared/config/model/palette';

import {
    AVAILABILITY_THRESHOLD,
    DOMAIN_DAYS_THRESHOLD,
    SSL_DAYS_THRESHOLD,
    daysUntil,
    isAvailabilityOk,
} from '../../../lib/statusPageHelper';
import {IStatusPageViewMonitor} from '../../../model/IStatusPageView';

import StatusIncidents from '../StatusIncidents/StatusIncidents';

import styles from './StatusMonitorDetails.module.scss';

interface IStatusMonitorDetailsProps {
    monitor: IStatusPageViewMonitor,
}

interface IMetric {
    label: string,
    value: string,
    ok: boolean | null,
}

const dateFormat = !isTranslate() ? 'DD.MM.YYYY' : 'YYYY-MM-DD';

const StatusMonitorDetails: React.FC<IStatusMonitorDetailsProps> = ({monitor}) => {
    const metrics: IMetric[] = [
        {
            label: t`Доступность 24ч`,
            value: `${monitor.availability1}%`,
            ok: isAvailabilityOk(monitor.availability1, AVAILABILITY_THRESHOLD.d1),
        },
        {
            label: t`Доступность 7д`,
            value: `${monitor.availability7}%`,
            ok: isAvailabilityOk(monitor.availability7, AVAILABILITY_THRESHOLD.d7),
        },
        {
            label: t`Доступность 30д`,
            value: `${monitor.availability30}%`,
            ok: isAvailabilityOk(monitor.availability30, AVAILABILITY_THRESHOLD.d30),
        },
    ];

    if (monitor.ssl_watch) {
        metrics.push({
            label: t`SSL до`,
            value: monitor.ssl_active_to ? getFormattedDate(monitor.ssl_active_to, {format: dateFormat}) : '—',
            ok: monitor.ssl_active_to ? daysUntil(monitor.ssl_active_to) > SSL_DAYS_THRESHOLD : null,
        });
    }

    if (monitor.domain_watch) {
        metrics.push({
            label: t`Домен до`,
            value: monitor.domain_active_to
                ? getFormattedDate(monitor.domain_active_to, {format: dateFormat})
                : '—',
            ok: monitor.domain_active_to ? daysUntil(monitor.domain_active_to) > DOMAIN_DAYS_THRESHOLD : null,
        });
    }

    return (
        <div className={styles.details}>
            <div className={styles.metrics}>
                {metrics.map((metric) => (
                    <div
                        key={metric.label}
                        className={cn(
                            styles.metric,
                            metric.ok === true && styles.metricOk,
                            metric.ok === false && styles.metricBad,
                        )}
                    >
                        <div className={styles.metricTop}>
                            <span className={styles.metricLabel}>{metric.label}</span>
                            {metric.ok !== null && (
                                <span className={styles.metricIcon}>
                                    {metric.ok ? <Correct color={PALETTE.success}/> : <Wrong/>}
                                </span>
                            )}
                        </div>
                        <span className={styles.metricValue}>{metric.value}</span>
                    </div>
                ))}
            </div>

            <div className={styles.chart}>
                <ResponseTimeChart
                    data={monitor.response_times_24h}
                    paused={monitor.status === 'paused'}
                    height={220}
                    disableAnimation
                />
            </div>

            <StatusIncidents incidents={monitor.recent_incidents}/>
        </div>
    );
};

export default StatusMonitorDetails;
